/* eslint-disable react/no-unescaped-entities */
import { Alert, CircularProgress } from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useShallow } from 'zustand/react/shallow'
import { useAuthentificationStore } from '../../contexts/AuthenticationStore.jsx'
import { useDayStore } from '../../contexts/DayStore'
import SessionExpiredModal from '../../components/SessionExpiredModal/SessionExpiredModal.jsx'
import {
  COLOR_VISION_GROUPS,
  useDayViewSettingsStore,
} from '../../contexts/DayViewSettingsStore.jsx'
import {
  SESSION_STATUS_AUTHENTICATED,
  SESSION_STATUS_EXPIRED,
  useSessionStore,
} from '../../contexts/SessionStore.jsx'
import { useSocketStore } from '../../contexts/SocketStore.jsx'
import { useThemeContext } from '../../contexts/ThemeProvider.jsx'
import { ROUTE_DAY_VIEW_HEADLESS } from '../../router/Routes.jsx'
import {
  COMMENT_STYLES,
  ROOM_PREFIX_DAY_EDIT,
  ROOM_PREFIX_DAY_VIEW,
} from '../../utils/constantes.js'
import dayjs, { PLANNING_TIME_ZONE } from '../../utils/dayjsHelper.js'
import { PERM_PLANNING_EDIT, PERM_PLANNING_EDIT_SHORT_PERIOD } from '../../utils/permissions.js'
import { sortSessions } from '../../utils/utils.js'
import ColumnsHeaders from './ColumnsHeaders/ColumnsHeaders.jsx'
import './DayView.css'
import DayViewControls from './DayViewControls.jsx'
import DayViewGroup from './DayViewGroup.jsx'
import DayViewSession from './DayViewSession.jsx'
import { useDateStore } from '../../contexts/DateStore.jsx'
import { useSessionKeepAlive } from '../../hooks/useSessionKeepAlive.js'
import { useKonamiCode } from '../../utils/customHooks.js'

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'

const DayView = ({ isFullscreen = false }) => {
  const [searchParams] = useSearchParams()
  const placeParam = searchParams.get('place_id')
  const placeFixed = placeParam !== null ? parseInt(placeParam) : false
  const [placeId, setPlaceId] = useState(placeFixed)
  const [isVisible, setIsVisible] = useState(false)
  const timerVisible = useRef(null)
  const ignoreWakeUntil = useRef(0)
  const isTablet = searchParams.get('isTablet') !== null
  const location = useLocation()
  const isHeadLess = location.pathname.includes(ROUTE_DAY_VIEW_HEADLESS) || isFullscreen
  const { date } = useDateStore(
    useShallow((state) => ({
      date: state.date,
    }))
  )
  const { day, isDayLoading } = useDayStore(
    useShallow((state) => ({
      day: state.day,
      isDayLoading: state.isDayLoading,
    }))
  )
  const { hasPerm } = useAuthentificationStore(
    useShallow((state) => ({
      hasPerm: state.hasPerm,
    }))
  )
  const { printGroups, setColorVision, setPrintGroups } = useDayViewSettingsStore(
    useShallow((state) => ({
      printGroups: state.printGroups,
      setColorVision: state.setColorVision,
      setPrintGroups: state.setPrintGroups,
    }))
  )
  const { socket, setConnectedRoom, setNoNeedLogin, isSocketConnected, socketAuthError } =
    useSocketStore(
      useShallow((state) => ({
        socket: state.socket,
        setConnectedRoom: state.setConnectedRoom,
        setNoNeedLogin: state.setNoNeedLogin,
        isSocketConnected: state.isSocketConnected,
        socketAuthError: state.socketAuthError,
      }))
    )
  const { sessionStatus } = useSessionStore(
    useShallow((state) => ({
      sessionStatus: state.status,
    }))
  )
  const { setPreferDark } = useThemeContext()

  useKonamiCode(() => {
    const cells = document.getElementsByClassName('sessionDayView')
    Array.from(cells).forEach((cell) => cell.classList.add('konami'))
  })

  useEffect(() => {
    setNoNeedLogin(isTablet)
  }, [setNoNeedLogin, isTablet])

  // Force white theme for headless mode ( print compatibility )
  useEffect(() => {
    if (isHeadLess) {
      setPreferDark(false)
      setPrintGroups(true)
      setColorVision(COLOR_VISION_GROUPS)
    }
  }, [isHeadLess, setColorVision, setPreferDark, setPrintGroups])

  const canEditFromPermissions = useMemo(() => {
    if (hasPerm(PERM_PLANNING_EDIT)) {
      return !isHeadLess && day?.isReal
    }
    date.hour(0)
    const now = dayjs()
    const diff = date.diff(now, 'hour')
    // - 24 - 3 : on peut modifier j-1 jusqu'a j0 à 3h du mat
    if (-24 - 3 <= diff && day?.isReal) {
      return !isHeadLess && hasPerm(PERM_PLANNING_EDIT_SHORT_PERIOD)
    }

    return false
  }, [date, hasPerm, isHeadLess, day])

  const canEdit =
    canEditFromPermissions &&
    sessionStatus === SESSION_STATUS_AUTHENTICATED &&
    isSocketConnected &&
    !socketAuthError

  const canCheck = useMemo(() => {
    return !isHeadLess && (hasPerm(PERM_PLANNING_EDIT) || hasPerm(PERM_PLANNING_EDIT_SHORT_PERIOD))
  }, [hasPerm, isHeadLess])

  useSessionKeepAlive(canEdit)

  const sessionWarning = useMemo(() => {
    if (sessionStatus === SESSION_STATUS_EXPIRED) {
      return 'Session expirée. Reconnectez-vous pour modifier la journée.'
    }
    if (socketAuthError) {
      return 'Connexion temps réel non authentifiée. Reconnectez-vous pour modifier la journée.'
    }
    if (canEditFromPermissions && !isSocketConnected) {
      return 'Connexion temps réel interrompue. Édition suspendue.'
    }
    return null
  }, [canEditFromPermissions, isSocketConnected, sessionStatus, socketAuthError])

  const powerOff = useCallback(() => {
    ignoreWakeUntil.current = Date.now() + 500
    clearTimeout(timerVisible.current)
    setIsVisible(false)
  }, [])

  const handleAnyAction = () => {
    if (Date.now() < ignoreWakeUntil.current) return
    if (!isVisible) {
      setIsVisible(true)
    }
    clearTimeout(timerVisible.current)
    timerVisible.current = setTimeout(() => {
      setIsVisible(false)
    }, 30000)
  }

  useEffect(() => {
    if (socket && day?.date) {
      socket?.emit('joinPage.dayView', day?.date, canEdit, () => {
        const prefix = canEdit ? ROOM_PREFIX_DAY_EDIT : ROOM_PREFIX_DAY_VIEW
        console.log('Connected to room', prefix + day?.date)
        setConnectedRoom(prefix + day?.date)
      })
    }
    // When leaving the page
    return () => {
      socket?.emit('leavePage.dayView')
      setConnectedRoom(null)
    }
  }, [socket, day?.date, setConnectedRoom, canEdit])

  useEffect(() => {
    console.log('day', day)
  }, [day])

  const maxGroupOfDay = useMemo(() => {
    if (!day) return 0
    return Math.max(
      ...day.intervals.map((interval) => {
        const gpCardi = interval.groups.filter((g) => g.place_id === 1)
        const gpStMich = interval.groups.filter((g) => g.place_id === 2)
        return Math.max(gpCardi.length, gpStMich.length)
      })
    )
  }, [day])

  const sequences = useMemo(() => {
    const allIntervals = {}
    day?.intervals.forEach((interval) => {
      const uniqEmailForInterval = []
      interval.sessions.forEach((session) => {
        const email = session?.booking?.email
        if (email) {
          const index = uniqEmailForInterval.findIndex((e) => e.email === email)
          if (index === -1) {
            uniqEmailForInterval.push({ email, sessions: [session._id] })
          } else {
            uniqEmailForInterval[index].sessions.push(session._id)
          }
        }
      })
      allIntervals[interval._id] = Array.from(uniqEmailForInterval)
    })

    const emailCounts = {}
    Object.values(allIntervals).forEach((array) => {
      array.forEach((item) => {
        const email = item.email
        if (emailCounts[email]) {
          emailCounts[email]++
        } else {
          emailCounts[email] = 1
        }
      })
    })
    const seqs = []
    Object.values(allIntervals).forEach((array) => {
      array.forEach((item) => {
        const email = item.email
        if (emailCounts[email] > 1) {
          const prevSeq = seqs.find((s) => s.email === email)
          if (prevSeq) {
            prevSeq.sessions.push(...item.sessions)
          } else {
            seqs.push({ email, sessions: [...item.sessions] })
          }
        }
      })
    })

    return seqs.map((seq, index) => ({
      ...seq,
      letter: `${ALPHABET[index % 26]}${index >= 26 ? 2 : ''}${index >= 26 * 2 ? 3 : ''}`,
    }))
  }, [day?.intervals])

  const renderCommentariesDay = useCallback(
    (place_id) => {
      if (isDayLoading || !day) return <></>
      const commentaries = day.commentaries.filter((c) => c.place_id === place_id)
      if (commentaries.length === 0) return <></>
      return (
        <div className="dayCommentaries">
          {commentaries.map((commentary, i) => {
            const styles = COMMENT_STYLES.find((s) => s.id === commentary.styleId)?.styles
            return (
              <div key={i} className="dayCommentary" style={styles}>
                {commentary.text}
              </div>
            )
          })}
        </div>
      )
    },
    [isDayLoading, day]
  )

  const renderAdditionnalTimes = useCallback(() => {
    if (isDayLoading || !day || !day.isReal) return <></>

    const user = useAuthentificationStore.getState().user
    const additionalTimes = day.additionalTimes.filter((at) => at.user === user._id)
    if (additionalTimes.length === 0) return <></>
    const total = additionalTimes.reduce((acc, at) => acc + at.duration, 0) // in ms
    const hours = Math.floor(total / 3600000)
    const minutes = Math.floor((total % 3600000) / 60000)
    const str = `${hours}h${minutes < 10 ? '0' : ''}${minutes}`
    return (
      <Alert sx={{ mb: 1 }} severity="info">
        On t'a compté {str} de temps additionnel pour la journée.
      </Alert>
    )
  }, [isDayLoading, day])

  const renderDay = useCallback(
    (place_id) => {
      if (isDayLoading) {
        return (
          <div className="loadingWrapper">
            <CircularProgress size={100} />
          </div>
        )
      }
      if (!!day) {
        return day.intervals
          .toSorted((a, b) => new Date(a.start) - new Date(b.start))
          .map((interval, i) => {
            const filteredSortedSessions = sortSessions(
              interval.sessions.filter((s) => s?.place_id === place_id)
            )
            const horaire = dayjs(interval.start)
              .tz(PLANNING_TIME_ZONE)
              .add(i === 0 ? -15 : -10, 'minutes')
              .format('HH:mm')
            return (
              <div key={i} className="dayViewLine">
                <div className="sessionDayView horaire">{horaire}</div>
                {filteredSortedSessions.map((s) => (
                  <DayViewSession
                    dayId={day._id}
                    intervalId={interval._id}
                    key={s._id}
                    session={s}
                    intervalGroups={interval.groups.filter((g) => g.place_id === place_id)}
                    canEdit={canEdit}
                    sequences={sequences}
                  />
                ))}
                {printGroups &&
                  [...new Array(maxGroupOfDay || 0)].map((_, groupIndex) => (
                    <DayViewGroup
                      dayId={day._id}
                      key={groupIndex}
                      interval={interval}
                      group={interval.groups.filter((g) => g.place_id === place_id)[groupIndex]}
                      groupIndex={groupIndex}
                      place_id={place_id}
                      canEdit={canEdit}
                    />
                  ))}
              </div>
            )
          })
      } else {
        return (
          <div className="noPlanning">
            {"Ce planning n'est pas disponible, choisissez une autre date."}
          </div>
        )
      }
    },
    [isDayLoading, day, printGroups, maxGroupOfDay, canEdit, sequences]
  )
  return (
    <div
      className={`dayViewContainer ${isHeadLess && 'centered'} ${isTablet && 'isTablet'} ${
        isVisible && 'isVisible'
      }`}
      style={
        isFullscreen
          ? {
              position: 'absolute',
              top: '50%',
              width: '100%',
              transform: 'translateY(-50%) scale(1.4)',
            }
          : {}
      }
      onMouseMove={handleAnyAction}
    >
      <DayViewControls
        isHeadLess={isHeadLess}
        canEdit={canEdit}
        canCheck={canCheck}
        isTablet={isTablet}
        isFullscreen={isFullscreen}
        placeId={placeId}
        setPlaceId={setPlaceId}
        powerOff={powerOff}
      />
      {!isHeadLess && sessionWarning && (
        <Alert sx={{ mb: 1 }} severity="warning">
          {sessionWarning}
        </Alert>
      )}
      {!isHeadLess && !day?.isReal && (
        <div className="warningMessage">
          🦦 Attention les loutres ! 🦦
          <br /> Ce planning n'est qu'une prédiction et pourrait être modifié, n'hésitez pas à
          revenir le voir !{' '}
        </div>
      )}
      {!isHeadLess && renderAdditionnalTimes()}
      {(!placeId || placeId === 1) && (
        <>
          <ColumnsHeaders place={1} nbGroups={maxGroupOfDay} />
          {renderDay(1)}
          {!isHeadLess && renderCommentariesDay(1)}
        </>
      )}
      {!isTablet && <div className="spacer" />}
      {(!placeId || placeId === 2) && (
        <>
          <ColumnsHeaders place={2} nbGroups={maxGroupOfDay} />
          {renderDay(2)}
          {!isHeadLess && renderCommentariesDay(2)}
        </>
      )}
      <SessionExpiredModal />
    </div>
  )
}
export default DayView
